import { EReferralGqlService } from './services/ereferral.gql-service';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideServiceWorker } from '@angular/service-worker';
import { appRoutes } from './app.routes';
import { provideIt2GoTranslateService } from './app-translate.config';
import { environment } from '../environments/environment';
import { languageInitializer } from './initializers/language.initializer';
import { provideChildTranslateService } from '@ngx-translate/core';
import {
    provideRouter,
    withEnabledBlockingInitialNavigation,
    withInMemoryScrolling,
    withRouterConfig,
} from '@angular/router';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { BreadcrumbService } from 'xng-breadcrumb';
import { organizationInterceptor } from './http-interceptors/organization.interceptor';
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from '@openng/optimus-ui/dynamicdialog';
import { NavigatorGqlService } from './services/navigator.gql-service';
import { authInterceptor } from './modules/inbox/service/interceptors/auth.interceptor';
import './utils/observable.utils';
import './utils/form.utils';
import './utils/behavioral-subject.utils';
import './utils/dialog.utils';
import './utils/work.utils';
import { GQL_TOKEN } from './services/global.gql-service';
import { SchedulerGqlServiceV2 } from './services/scheduler.gql-service-v2';
import { ServiceGqlServiceV2 } from './services/service.gql-service';
import { ServiceSettingsGqlServiceV2 } from './services/service-settings.gql-service-v2';
import { FolderGqlServiceV2 } from './services/folder.gql-service-v2';
import { PersonGqlServiceV2 } from './services/person.gql-service-v2';
import { UserProfileGqlService } from './services/user-profile.gql-service';
import { EmploymentGqlService } from './services/employment.gql-service';
import { UserGqlService } from './services/user.gql-service';
import { WorkGqlService } from './services/work.gql-service';
import { ExpertiseGqlServiceV2 } from './services/expertise.gql-service-v2';
import { AddressBookGqlService } from './services/address-book.gql-service';
import { ToolsGqlService } from './services/tools.gql-service';
import { WorkplaceGqlServiceV2 } from './services/workplace.gql-service-v2';
import { NursingCareGqlServiceV2 } from './services/nursing-care.gql-service-v2';
import { VoucherGqlServiceV2 } from './services/voucher.gql-service-v2';
import { DashboardGqlService } from './services/dashboard.gql-service';
import { EditorialGqlService } from './services/editorial.gql-service';
import { EDITORIAL_API } from './services/editorial-api.token';
import { FormGqlServiceV2 } from './services/form.gql-service-v2';
import { SettingsGqlService } from './services/settings.gql-service';
import { MedicationGqlService } from './services/medication.gql-service';
import { PersonAddressServiceV2 } from './services/person-address.qgl-service-v2';
import { ObjectHistoryGqlService } from './services/object-history.gql-service';
import { EPrescriptionGqlService } from './services/e-prescription.gql-service';
import { EPoukazGqlService } from './services/e-poukaz.gql-service';
import { FolderSickNotesGqlService } from './services/folder-sick-notes.gql.service';
import { StatisticsGqlService } from './services/statistics.gql-service';
import { PuroGqlService } from './services/puro.gql-service';
import { PmupGqlService } from './services/pmup.gql-service';
import { OrganizationGqlService } from './services/organization.gql-service';
import { FiscalYearGqlService } from './services/fiscal-year.gql-service';
import { MadoAccountServiceV2 } from './services/mado/account.gql-service-v2';
import { DepartmentGqlService } from './services/department.gql-service';
import { ProjectGqlService } from './services/project.gql-service';
import { BudgetChapterGqlService } from './services/budget-chapter.gql-service';
import { VzpGqlServiceV2 } from './services/vzp.gql-service-v2';
import { CurrencyGqlServiceV2 } from './services/currency.gql-service-v2';
import { InsuranceCompanyGqlServiceV2 } from './services/insurance-company.gql-service-v2';
import { AccountingJournalGqlServiceV2 } from './services/accouting-journal.gql-service-v2';
import { VatCodeGqlService } from './services/vat-code.gql-service';
import { VatCodeLineGqlService } from './services/vat-code-line.gql-service';
import { OrganizationCenterPersonGqlService } from './services/organization-center-person.gql-service';
import { JournalGqlService } from './services/journal.gql-service';
import { ReconciliationGqlService } from './services/reconciliation.gql-service';
import { AnamnesisGqlServiceV2 } from './services/anamnesis.gql-service-v2';
import { ServiceCertificatesGqlService } from './services/service-certificates.gql.service';
import { FileGqlService } from './services/file.gql-service';
import { UserSecretsGqlService } from './services/user-secrets.gql-service';
import { AccountingReportGqlService } from './services/accounting-report.gql-service';
import { ExportImportService } from './services/export-import.gql-service';
import { MadoReportService } from './services/mado';
import { ListMunicipalityService } from './services/list/municipalicty.gql-service';
import { ListSszRegionService } from './services/list/ssz-region.gql-service';
import { OperationalReportTypeGqlService } from './services/operational-report-type.gql-service';
import { ListNameDayService } from './services/list/name-day.gql-service';
import { ServiceTypeSettingsGqlService } from './services/service-type-settings.gql-service';
import { EmailHistoryGqlService } from './services/email-history.gql-service';
import { CarePaymentGqlServiceV2 } from './services/care-payment.gql-service-v2';
import { SocialFinancialDepositGqlServiceV2 } from './services/social-financial-deposit.gql-service-v2';
import { SocialPaymentGqlServiceV2 } from './services/social-payment.gql-service-v2';
import { BillingGqlServiceV2 } from './services/billing.gql-service-v2';
import { CollectionGqlService } from './modules/crm/service/collection.gql-service';
import { DonorGqlService } from './modules/crm/service/donor.gql-service';
import { DonationGqlService } from './modules/crm/service/donation.gql-service';
import { PersonDependenceGqlServiceV2 } from './services/person-dependence.gql-service-v2';
import { LoanAgreementGqlService } from './services/loan-agreement.gql-service';
import { provideOptimus } from '@openng/optimus-ui/config';
import it2goPreset from './theme/it2go.preset';
import { ListMpsvTargetGroupService } from './services/list/mpsv-target-group.gql-service';
import { SocialServiceOfferGroupGqlServiceV2 } from './services/social-service-offer-group.gql-service';
import { OFFLINE_DB_TOKEN } from './store/offline-db/object.db';
import { PersonDb } from './store/offline-db/person.db';
import { ServiceDb } from './store/offline-db/service.db';
import { FolderDb } from './store/offline-db/folder.db';
import { UserDb } from './store/offline-db/user.db';
import { EmploymentDb } from './store/offline-db/employment.db';
import { OrganizationDb } from './store/offline-db/organization.db';
import { OrganizationSettingDb } from './store/offline-db/organization-setting.db';
import { ServiceVisitTypeDb } from './store/offline-db/service-visit-type.db';
import { FolderMedicationDb } from './store/offline-db/folder-medication.db';
import { ServiceMedicalProcedureDb } from './store/offline-db/service-medical-procedure.db';
import { LoanPaymentGqlService } from './services/loan-payment.gql-service';
import { LoanAgreementLineGqlService } from './services/loan-agreement-line.gql-service';
import { LoanAgreementReminderGqlService } from './services/loan-agreement-reminder.gql-service';
import { LoanAgreementPreceptGqlService } from './services/loan-agreement-percept.gql-service';
import { AbStatsGqlService } from './modules/ab-stats/service/ab-stats.gql-service';
import { VoucherDb } from './store/offline-db/voucher.db';
import { ServiceSpecialistRecordContactTypeDb } from './store/offline-db/service-specialist-record-contact-type.db';
import { ServiceSpecialistRecordWorkTypeDb } from './store/offline-db/service-specialist-record-work-type.db';
import { DiagnosisDb } from './store/offline-db/diagnosis.db';
import { AbStatsReportGqlService } from './modules/ab-stats/service/ab-stats-report.gql-service';
import { MedicationDb } from './store/offline-db/medication.db';
import { LoanAgreementTransactionGqlService } from './services/loan-agreement-transaction.gql-service';
import { AuthorizationGqlService } from './services/authorization.gql-service';
import { InitializerService } from './modules/layout/services/initializer.service';
import { NotificationsGqlService } from './modules/layout/services/notifications-gql.service';
import { ApiService as LayoutApiService } from './modules/layout/services/api.service';
import { LayoutServicesService } from './modules/layout/services/service.service';
import { NotificationsService } from './modules/layout/services/notifications.service';
import { SearchService } from './modules/layout/services/search.service';
import { LayoutEffects } from './modules/layout/store/layout.effects';
import { layoutStore } from './modules/layout/store/layout.reducer';
import { NotificationEffects } from './modules/layout/store/notifications/notifications.effects';
import { notificationsReducer } from './modules/layout/store/notifications/notifications.reducer';
import { notificationsFeatureKey } from './modules/layout/store/notifications/notifications.state';
import { apolloLinkProvider } from './modules/shared/apollo-links/apollo-link.provider';
import { MapService } from './modules/shared/components/map/map.service';
import { ConfirmBuilder } from './modules/shared/service/confirm.builder';
import { UserService as SharedUserService } from './modules/shared/service/user.service';
import { SharedGlobalService } from './modules/shared/shared.global-service';
import { SharedEffects } from './modules/shared/store/shared.effects';
import { sharedReducer } from './modules/shared/store/shared.reducer';
import { sharedFeatureKey } from './modules/shared/store/shared.state';
import { FormatVoucherPipe } from './modules/shared/pipes/format-voucher.pipe';
import { LocalDatePipe } from './modules/shared/pipes/local-date.pipe';
import { userReducer } from './modules/content/store/user/user.reducer';
import { userFeatureKey } from './modules/content/store/user/user.state';
import { ORGANIZATION_GLOBAL_STORE_TOKEN } from './store/types/organization.global-store.type';
import { OrganizationGlobalStore } from './store/organization.global-store';
import { LoggedUserGlobalStore } from './store/logged-user.global-store';
import { LOGGED_USER_GLOBAL_STORE_TOKEN } from './store/types/logged-user.global-store.type';
import { GLOBAL_STORE_TOKEN } from './store/types/global-store.type';
import { GlobalStore } from './store/global.global-store';
import { InboxGqlService } from './modules/inbox/service/inbox-gql.service';
import { InboxCountsGqlService } from './modules/inbox/service/inbox-counts.gql-service';
import { DataboxOfficialGqlService } from './modules/inbox/service/databox-official.gql-service';
import { DataboxDmsMessageGqlService } from './modules/inbox/service/databox-dms-message.gql-service';
import { InboxMessageFromAfFileGqlService } from './modules/inbox/service/inbox-message-from-af-file.gql-service';
import { InboxToOrganizationGqlService } from './modules/inbox/service/inbox-to-organization.gql-service';
import { InboxToUserGqlService } from './modules/inbox/service/inbox-to-user.gql-service';
import { InboxOrganizationGqlService } from './modules/inbox/service/inbox-organization.gql-service';
import { InboxUserGqlService } from './modules/inbox/service/inbox-user.gql-service';
import {
    DataboxDmsMessageAttachmentGqlService,
} from './modules/inbox/service/databox-dms-message-attachment.gql-service';
import { InboxLabelGqlService } from './modules/inbox/service/inbox-label.gql-service';
import { InboxUserSettingsGqlService } from './modules/inbox/service/inbox-user-settings-gql.service';
import { EmailAddressGqlService } from './modules/inbox/service/email-address.gql-service';
import { EmailMessageGqlService } from './modules/inbox/service/email-message.gql-service';
import { EmailMessageAttachmentGqlService } from './modules/inbox/service/email-message-attachment.gql-service';
import { BasicSocialConsultingGqlService } from './services/basic-social-consulting-gql.service';
import { FolderIndividualPlanGqlService } from './services/folder-individual-plan.gql-service';
import { UserTaskGqlService } from './services/user-task.gql-service';
import { ServiceDocumentSequenceGqlService } from './services/service-document-sequence.gql-service';
import { FolderContractsGqlService } from './services/folder-contracts.gql-service';
import { BreadcrumbMenuGqlService } from './services/breadcrumb.menu.gql-service';
import { InboxOrderGqlService } from './modules/inbox/service/inbox-order.gql-service';
import { DietGqlService } from './services/diet.gql-service';
import { ServiceTimeBandGqlService } from './services/service-time-band.gql-service';
import { FolderSocialAcquireProcessGqlService } from './services/folder-social-acquire-process.gql-service';
import { WaitingListGqlService } from './services/waiting-list-gql.service';
import { WaitingListRatingGqlService } from './services/waiting-list-rating-gql.service';
import { GuidePageGqlService } from './services/guide-page-gql.service';
import { GuidePageViewGqlService } from './services/guide-page-view-gql.service';
import { WarehouseMaterialGqlService } from './services/warehouse-material.gql-service';
import { WarehouseMaterialOperationGqlService } from './services/warehouse-material-operation.gql-service';
import { WorkDataServiceAnnouncementGqlServiceV2 } from './services/service-announcement.gql-service-v2';
import { DiagnosisGqlService } from './services/diagnosis.gql-service';
import { DataInterfaceVersionGqlService } from './services/datainterface-version.gql-service';
import { SystemGqlService } from './services/system.gql-service';
import { MessageGqlService } from './services/message.gql-service';
import { CalendarItemGqlService } from './services/calendar-item.gql-service';
import { SocialBillingGqlService } from './services/social-billing.gql-service';
import { ServiceSocialProjectGqlService } from './services/service-social-project.gql-service';
import { ServiceSocialProjectClientGqlService } from './services/service-social-project-client.gql-service';
import { ServiceSocialProjectUserGqlService } from './services/service-social-project-user.gql-service';
import { ServiceWorkOfferProjectGqlService } from './services/service-work-offer-project.gql-service';
import { MonitoringSheetGqlService } from './services/service-monitoring-sheet.gql-service';
import { DeskaMonitorGqlService } from './services/deska-monitor/deska-monitor.gql-service';
import { GlobalEffects } from './store/global/global.effects';
import { globalReducer } from './store/global/global.reducer';
import { globalFeatureKey } from './store/global/global.state';
import { EducationGqlService } from './services/education.gql-service';
import { EmploymentTypeEducationGqlService } from './services/employment-type-education.gql-service';
import { ListSuklAdministrationRouteService } from './services/list/sukl-administration-route.gql-service';
import { ListSuklPharmaceuticalFormService } from './services/list/sukl-pharmaceutical-form.gql-service';
import { CalendarCheckoutGqlService } from './services/calendar-checkout.gql-service';
import { WorkRecorderGqlService } from './services/work-recorder.gql-service';
import { ListSuklEPoukazCatalogService } from './services/list/sukl-epoukaz-catalog.gql-service';
import { AttendanceGqlService } from './services/attendance.gql-service';
import { AttendancePrescriptionGqlService } from './services/attendance-prescription.gql-service';
import { FolderNursingAllowanceGqlService } from './services/folder-nursing-allowance.gql-service';
import { MicrosoftGraphKeysSettingGqlService } from './modules/inbox/service/microsoft-graph-keys-setting.gql-service';
import { SocialCreditGqlService } from './services/social-credit.gql-service';
import { IplpRecipeGqlService } from './services/iplp-recipe.gql-service';
import { TrashGqlService } from './services/trash.gql-service';
import { KrzpDoctorGqlService } from './services/krzp-doctor.gql-service';
import { PlatformBillingGqlService } from './services/platform-billing.gql-service';

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection(),
        provideRouter(
            appRoutes,
            withEnabledBlockingInitialNavigation(),
            withRouterConfig({ paramsInheritanceStrategy: 'always' }),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
        ),
        provideHttpClient(
            // TODO withXhr() z angular migrace 21->22 - tohle je pro report progressu, který nejspíše není potřeba - odstranit a vyzkoušet upload/download, defaultně to v Agnular 22 už není
            withXhr(),
            withInterceptors([
                organizationInterceptor,
                authInterceptor,
            ]),
        ),
        provideAnimations(),
        provideStore({
            [globalFeatureKey]: globalReducer,
            router: routerReducer,
        }),
        provideEffects(
            GlobalEffects,
            LayoutEffects,
            NotificationEffects,
            SharedEffects,
        ),
        !environment.production ? provideStoreDevtools({ connectInZone: true }) : [],
        provideState(notificationsFeatureKey, notificationsReducer),
        provideState(layoutStore),
        provideState(sharedFeatureKey, sharedReducer),
        provideState(userFeatureKey, userReducer),
        provideRouterStore(),
        provideAngularSvgIcon(),
        provideChildTranslateService({ extend: true }),
        ConfirmationService,
        languageInitializer,
        BreadcrumbService,
        MessageService,
        provideIt2GoTranslateService(),
        LayoutApiService,
        apolloLinkProvider,
        NotificationsService,
        LayoutServicesService,
        SearchService,
        MapService,
        SharedUserService,
        DialogService,
        DynamicDialogRef,
        DynamicDialogConfig,
        ConfirmBuilder,
        provideAppInitializer(() => {
            inject(SharedGlobalService);
        }),
        FormatVoucherPipe,
        LocalDatePipe,
        provideOptimus(
            {
                theme: {
                    preset: it2goPreset,
                    options: {
                        darkModeSelector: '.theme-dark',
                    },
                },
                ripple: true,
            },
        ),
        provideAppInitializer(() => {
            const initService = inject(InitializerService);
            initService.load().then();
        }),
        {
            provide: ORGANIZATION_GLOBAL_STORE_TOKEN,
            useExisting: OrganizationGlobalStore,
        },
        {
            provide: LOGGED_USER_GLOBAL_STORE_TOKEN,
            useExisting: LoggedUserGlobalStore,
        },
        {
            provide: GLOBAL_STORE_TOKEN,
            useExisting: GlobalStore,
        },
        { provide: GQL_TOKEN, useExisting: SchedulerGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: ServiceGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: ServiceSettingsGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: FolderGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: PersonGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: UserProfileGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: EmploymentGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: UserGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: NursingCareGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: InsuranceCompanyGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: VoucherGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: WorkGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ExpertiseGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: AddressBookGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ToolsGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: WorkplaceGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: VzpGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: DashboardGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: EditorialGqlService, multi: true },
        { provide: EDITORIAL_API, useExisting: EditorialGqlService },
        { provide: GQL_TOKEN, useExisting: FormGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: SettingsGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: MedicationGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: DiagnosisGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: DataInterfaceVersionGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: PersonAddressServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: ObjectHistoryGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: EPrescriptionGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: FolderSickNotesGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: StatisticsGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: PuroGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: PmupGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: OrganizationGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: FiscalYearGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: MadoAccountServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: DepartmentGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ProjectGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: BudgetChapterGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: CurrencyGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: AccountingJournalGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: VatCodeGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: VatCodeLineGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: OrganizationCenterPersonGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: JournalGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: LoanAgreementGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: LoanAgreementLineGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: LoanAgreementReminderGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: LoanAgreementTransactionGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: LoanPaymentGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: LoanAgreementPreceptGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ExportImportService, multi: true },
        { provide: GQL_TOKEN, useExisting: AccountingReportGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ReconciliationGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: AnamnesisGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: ServiceCertificatesGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: FileGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: UserSecretsGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: MadoReportService, multi: true },
        { provide: GQL_TOKEN, useExisting: ListMunicipalityService, multi: true },
        { provide: GQL_TOKEN, useExisting: ListSszRegionService, multi: true },
        { provide: GQL_TOKEN, useExisting: ListNameDayService, multi: true },
        { provide: GQL_TOKEN, useExisting: OperationalReportTypeGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ServiceTypeSettingsGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: EmailHistoryGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: CarePaymentGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: SocialFinancialDepositGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: SocialPaymentGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: PersonDependenceGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: BillingGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: CollectionGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: DonorGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: DonationGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ListMpsvTargetGroupService, multi: true },
        { provide: GQL_TOKEN, useExisting: SocialServiceOfferGroupGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: AuthorizationGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: NotificationsGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: NavigatorGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: BasicSocialConsultingGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: AbStatsGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: AbStatsReportGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: InboxCountsGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: InboxGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: DataboxOfficialGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: InboxOrderGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: DataboxDmsMessageGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: InboxMessageFromAfFileGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: InboxToOrganizationGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: InboxToUserGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: InboxOrganizationGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: InboxUserGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: DataboxDmsMessageAttachmentGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: InboxLabelGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: InboxUserSettingsGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: EmailAddressGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: EmailMessageGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: EmailMessageAttachmentGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: FolderIndividualPlanGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: UserTaskGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: SystemGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ServiceDocumentSequenceGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: SocialBillingGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: FolderContractsGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: BreadcrumbMenuGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: DietGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ServiceTimeBandGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: FolderSocialAcquireProcessGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: WaitingListGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: WaitingListRatingGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: GuidePageGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: GuidePageViewGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: WarehouseMaterialGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: WarehouseMaterialOperationGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: WorkDataServiceAnnouncementGqlServiceV2, multi: true },
        { provide: GQL_TOKEN, useExisting: ServiceSocialProjectGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ServiceSocialProjectClientGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ServiceSocialProjectUserGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ServiceWorkOfferProjectGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: MonitoringSheetGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: DeskaMonitorGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: MessageGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: CalendarItemGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: CalendarCheckoutGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: WorkRecorderGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: AttendanceGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: AttendancePrescriptionGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: EducationGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: EmploymentTypeEducationGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: ListSuklAdministrationRouteService, multi: true },
        { provide: GQL_TOKEN, useExisting: ListSuklPharmaceuticalFormService, multi: true },
        { provide: GQL_TOKEN, useExisting: ListSuklEPoukazCatalogService, multi: true },
        { provide: GQL_TOKEN, useExisting: EPoukazGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: FolderNursingAllowanceGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: MicrosoftGraphKeysSettingGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: SocialCreditGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: IplpRecipeGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: TrashGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: EReferralGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: KrzpDoctorGqlService, multi: true },
        { provide: GQL_TOKEN, useExisting: PlatformBillingGqlService, multi: true },

        { provide: OFFLINE_DB_TOKEN, useExisting: PersonDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: ServiceDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: FolderDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: UserDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: EmploymentDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: OrganizationDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: OrganizationSettingDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: ServiceVisitTypeDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: FolderMedicationDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: ServiceMedicalProcedureDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: VoucherDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: ServiceSpecialistRecordWorkTypeDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: ServiceSpecialistRecordContactTypeDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: DiagnosisDb, multi: true },
        { provide: OFFLINE_DB_TOKEN, useExisting: MedicationDb, multi: true },

        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000',
        }),
    ],
};
